// src/components/mascot/ElliWidget.js
// ─────────────────────────────────────────────────────────────
// Bulle flottante partout dans l'app une fois connecté. Pose une question
// en langage libre ("où sont les BCL?"), un Cloud Function (askElli,
// Claude côté serveur) choisit la destination la plus proche PARMI celles
// listées ci-dessous et y navigue directement — jamais une route inventée,
// le modèle ne peut choisir que dans cette liste. Repli local (mots-clés)
// si l'appel réseau échoue, pour ne jamais laisser le widget muet.
// ─────────────────────────────────────────────────────────────

import React, { useState, useRef, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import firebase from "firebase/app";
import "firebase/functions";
import { Box, Paper, TextField, Typography, IconButton, Chip, Fade } from "@mui/material";
import { Close as CloseIcon, Send as SendIcon } from "@mui/icons-material";
import Elli from "./Elli";
import { isHalloweenPeriod, isChristmasPeriod } from "./seasonal";

// Source de vérité unique des destinations connues — consommée à la fois
// pour le repli local (keywords) et envoyée telle quelle au Cloud Function
// (descriptionFr/descriptionEn) pour que le modèle choisisse parmi elles.
const DESTINATIONS = [
  { path: "/sprint", replyFr: "Direction Ellipse Sprint !", replyEn: "Off to Ellipse Sprint!", descriptionFr: "Les séances Sprint quotidiennes (Athlete/Emerging/Intermediate)", descriptionEn: "Daily Sprint sessions (Athlete/Emerging/Intermediate)", keywords: ["sprint", "athlete", "athlète", "emerging", "émergent"] },
  { path: "/sprint", replyFr: "Les Business Conversation Labs sont dans Ellipse Sprint, plus bas sur la page — je t'y amène.", replyEn: "Business Conversation Labs are inside Ellipse Sprint, further down the page — taking you there.", descriptionFr: "Les Business Conversation Labs (BCL), séances Mastermind aux deux semaines", descriptionEn: "Business Conversation Labs (BCL), biweekly Mastermind sessions", keywords: ["bcl", "business conversation", "conversation lab", "mastermind session", "labs"] },
  { path: "/mastermind", replyFr: "Voici ton espace Mastermind.", replyEn: "Here's your Mastermind space.", descriptionFr: "L'espace Mastermind (cohorte, onboarding, ressources)", descriptionEn: "The Mastermind space (cohort, onboarding, resources)", keywords: ["mastermind space", "espace mastermind", "stratégique", "strategic session"] },
  { path: "/coaches", replyFr: "Voici tes coachs.", replyEn: "Here are your coaches.", descriptionFr: "La liste des coachs et leurs liens de réservation", descriptionEn: "The list of coaches and their booking links", keywords: ["coach", "coachs", "coaches", "professeur", "prof"] },
  { path: "/schedule", replyFr: "Voici ton horaire.", replyEn: "Here's your schedule.", descriptionFr: "L'horaire des séances réservées", descriptionEn: "The schedule of booked sessions", keywords: ["horaire", "schedule", "calendrier", "agenda"] },
  { path: "/contracts", replyFr: "Voici tes contrats.", replyEn: "Here are your contracts.", descriptionFr: "Contrats et factures du membre", descriptionEn: "Member's contracts and invoices", keywords: ["contrat", "contract", "facture", "invoice"] },
  { path: "/history", replyFr: "Voici ton historique.", replyEn: "Here's your history.", descriptionFr: "Historique des séances passées", descriptionEn: "History of past sessions", keywords: ["historique", "history", "séances passées"] },
  { path: "/learningLogs", replyFr: "Voici le journal de tes séances.", replyEn: "Here's your session log.", descriptionFr: "Journal détaillé des séances (notes de coach)", descriptionEn: "Detailed session log (coach notes)", keywords: ["journal", "learning log", "log", "notes de séance"] },
  { path: "/resources", replyFr: "Voici les ressources.", replyEn: "Here are the resources.", descriptionFr: "Documents et ressources pédagogiques", descriptionEn: "Documents and learning resources", keywords: ["ressource", "resource", "document"] },
  { path: "/my-timesheet", replyFr: "Voici ta feuille de temps.", replyEn: "Here's your timesheet.", descriptionFr: "Feuille de temps du coach", descriptionEn: "Coach's timesheet", keywords: ["feuille de temps", "timesheet", "heures travaillées"] },
  { path: "/administration", replyFr: "Voici l'administration.", replyEn: "Here's administration.", descriptionFr: "Administration — gestion des membres (réservé admin)", descriptionEn: "Administration — member management (admin only)", keywords: ["administration", "admin", "gestion membres"] },
  { path: "/", replyFr: "Retour au tableau de bord.", replyEn: "Back to the dashboard.", descriptionFr: "Le tableau de bord principal", descriptionEn: "The main dashboard", keywords: ["dashboard", "tableau de bord", "accueil", "home"] },
];

function matchDestinationLocally(question) {
  const q = question.toLowerCase();
  let best = null, bestScore = 0;
  for (const dest of DESTINATIONS) {
    const score = dest.keywords.reduce((s, kw) => s + (q.includes(kw) ? 1 : 0), 0);
    if (score > bestScore) { best = dest; bestScore = score; }
  }
  return best;
}

// État "ouvert" sorti du composant — s'il se fait démonter/remonter pour
// une raison hors de notre contrôle (ex: un parent qui re-rend sur un
// changement d'auth/profile), la bulle ne doit pas se refermer sous les
// pieds du membre en pleine conversation.
let moduleOpenState = false;

// Contexte admin optionnel — posé par une page admin (ex: SearchUsers.js)
// quand une fiche membre est ouverte, lu par Elli au moment d'une
// question. Pas besoin d'état React, juste lu au clic sur Envoyer — et
// envoyé tel quel au serveur, qui décide SEUL (selon le vrai rôle de
// l'utilisateur authentifié) s'il doit s'en servir ou l'ignorer. Un
// membre ordinaire ne peut jamais faire poser ce contexte (aucune page
// membre n'appelle setElliSelectedUser), et même s'il l'envoyait lui-même
// via la console, le serveur l'ignorerait (voir elli_assistant.js).
let currentAdminSelectedUserId = null;
export function setElliSelectedUser(userId) {
  currentAdminSelectedUserId = userId || null;
}

export default function ElliWidget() {
  const { i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const history = useHistory();
  const location = useLocation();
  const [open, setOpenState] = useState(moduleOpenState);
  const setOpen = (value) => {
    setOpenState((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      moduleOpenState = next;
      return next;
    });
  };
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]); // { from: 'elli'|'you', text }
  const [talking, setTalking] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const theme = isHalloweenPeriod() ? "halloween" : isChristmasPeriod() ? "christmas" : null;

  // Nouvelle question à chaque ouverture — pas d'historique qui s'accumule,
  // c'est un raccourci de navigation, pas une vraie conversation à suivre.
  useEffect(() => {
    if (open) {
      const greeting = theme === "halloween"
        ? (fr ? "Bouh ! 🎃 Où est-ce que je peux t'amener ?" : "Boo! 🎃 Where can I take you?")
        : theme === "christmas"
          ? (fr ? "Ho ho ho ! 🎄 Où est-ce que je peux t'amener ?" : "Ho ho ho! 🎄 Where can I take you?")
          : (fr ? "Salut ! Où est-ce que je peux t'amener ?" : "Hi! Where can I take you?");
      setMessages([{ from: "elli", text: greeting }]);
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open, fr, theme]);

  // Défile vers le dernier message (ou les points "en train d'écrire") à
  // chaque nouveau tour — sinon la réponse arrive hors écran et il faut
  // scroller manuellement pour la voir.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, talking]);

  const sayNotSure = () => {
    setMessages((m) => [...m, { from: "elli", text: fr
      ? "Hmm, je ne suis pas sûre de trouver ça. Essaie \"Sprint\", \"BCL\", \"Coachs\", \"Contrats\"…"
      : "Hmm, not sure where that is. Try \"Sprint\", \"BCL\", \"Coaches\", \"Contracts\"…" }]);
  };

  const goTo = (path, reply) => {
    setMessages((m) => [...m, { from: "elli", text: reply }]);
    setTimeout(() => {
      history.push(path);
    }, 700);
  };

  const handleAsk = async () => {
    const q = question.trim();
    if (!q) return;
    setMessages((m) => [...m, { from: "you", text: q }]);
    setQuestion("");

    // Easter eggs — gérés avant l'appel réseau, pas besoin de déranger
    // Claude pour ça. Seulement pendant la période concernée.
    if (theme === "halloween" && /trick.?or.?treat|bonbon.?ou.?(un.?)?sort|^bonbons?$|^candy$/i.test(q)) {
      setMessages((m) => [...m, { from: "elli", text: fr
        ? "Un sort ! 🎃✨ (j'ai pas de bonbons, mais j'ai plein d'heures de coaching à t'offrir — où est-ce que je t'amène ?)"
        : "A spell! 🎃✨ (no candy here, but I've got coaching hours to offer — where can I take you?)" }]);
      return;
    }
    if (theme === "christmas" && /joyeux noël|joyeux noel|merry christmas|happy holidays|bonnes fêtes|bonnes fetes/i.test(q)) {
      setMessages((m) => [...m, { from: "elli", text: fr
        ? "🎄 À toi aussi ! Mon cadeau : je t'amène où tu veux en 2 secondes. Où est-ce qu'on va ?"
        : "🎄 Right back at you! My gift: I'll get you anywhere in 2 seconds. Where to?" }]);
      return;
    }

    // Capturé AVANT la mise à jour d'état — c'est l'historique jusqu'à
    // cette question, nécessaire pour qu'un "yes"/"oui" tout seul se
    // rattache à la proposition précédente d'Elli (sinon chaque question
    // est traitée isolément, et un suivi comme "oui" n'a aucun sens).
    const convoHistory = messages.slice(-8).map((m) => ({ role: m.from === "you" ? "user" : "assistant", text: m.text }));
    setTalking(true);

    try {
      const fn = firebase.functions().httpsCallable("askElli");
      const res = await fn({
        question: q,
        lang: fr ? "fr" : "en",
        history: convoHistory,
        destinations: DESTINATIONS.map((d) => ({ path: d.path, descriptionFr: d.descriptionFr, descriptionEn: d.descriptionEn })),
        selectedUserId: currentAdminSelectedUserId,
      });
      const { path, index, reply } = res.data;
      if (path) {
        // Le modèle (Haiku, choisi pour la vitesse) écrit parfois un texte
        // de réponse qui ne correspond pas à l'index qu'il a réellement
        // choisi (vu : dit "contracts", navigue vers "/schedule"). On
        // utilise le texte FIXE de la destination réellement choisie,
        // jamais le texte libre du modèle, pour garantir que ce qui
        // s'affiche corresponde toujours à où on amène le membre.
        const dest = (typeof index === "number" && DESTINATIONS[index]) ? DESTINATIONS[index] : null;
        goTo(path, dest ? (fr ? dest.replyFr : dest.replyEn) : reply);
      } else {
        setMessages((m) => [...m, { from: "elli", text: reply || (fr ? "Pose-moi une question sur l'app !" : "Ask me something about the app!") }]);
      }
    } catch (e) {
      console.warn("[Elli] askElli failed, falling back to local match:", e.message);
      const dest = matchDestinationLocally(q);
      if (dest) goTo(dest.path, fr ? dest.replyFr : dest.replyEn);
      else sayNotSure();
    } finally {
      setTalking(false);
    }
  };

  // Pas sur la page d'attente — le membre est sur le point d'entrer en
  // séance, une bulle flottante serait du bruit au pire moment.
  if (location.pathname.startsWith("/waiting/") || location.pathname.startsWith("/room/")) return null;

  return (
    <Box sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 1300 }}>
      {open && (
          <Fade in={open}>
            <Paper
              elevation={6}
              sx={{
                position: "absolute", bottom: 72, right: 0, width: 320, maxWidth: "88vw",
                borderRadius: 3, overflow: "hidden", display: "flex", flexDirection: "column",
              }}
            >
              <Box sx={{ bgcolor: "#1a1a2e", px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.25, position: "relative", overflow: "hidden" }}>
                {/* Petite toile dans le coin — touche perso, pas copiée d'ubee.com */}
                {theme === "halloween" && (
                  <Box sx={{ position: "absolute", top: -6, left: -6, opacity: 0.35, pointerEvents: "none" }}>
                    <svg width="38" height="38" viewBox="0 0 38 38">
                      <g stroke="#bfd56c" strokeWidth="1" fill="none">
                        <path d="M0 0 L38 38 M0 10 L28 38 M10 0 L38 28 M0 0 L0 20 M0 0 L20 0" />
                        <path d="M0 0 Q10 4 8 12 M0 0 Q4 10 12 8 M0 0 Q14 10 10 18" />
                      </g>
                    </svg>
                  </Box>
                )}
                {theme === "christmas" && (
                  <Typography sx={{ position: "absolute", top: 4, right: 40, fontSize: 16, opacity: 0.6, pointerEvents: "none" }}>❄</Typography>
                )}
                <Elli size={32} talking={talking} theme={theme} />
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 14, flexGrow: 1 }}>Elli</Typography>
                <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: "rgba(255,255,255,0.6)" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ p: 1.5, maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1, bgcolor: "#faf9f6" }}>
                {messages.map((m, i) => (
                  <Box
                    key={i}
                    sx={{
                      alignSelf: m.from === "elli" ? "flex-start" : "flex-end",
                      bgcolor: m.from === "elli" ? "#fff" : "#bfd56c",
                      border: m.from === "elli" ? "1px solid #e8e5df" : "none",
                      borderRadius: 2, px: 1.5, py: 0.9, maxWidth: "85%",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#1a1a2e", lineHeight: 1.5 }}>{m.text}</Typography>
                  </Box>
                ))}
                {talking && (
                  <Box sx={{ alignSelf: "flex-start", bgcolor: "#fff", border: "1px solid #e8e5df", borderRadius: 2, px: 1.5, py: 1.1, display: "flex", gap: 0.5 }}>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 6, height: 6, borderRadius: "50%", bgcolor: "#94a3b8",
                          animation: "elli-typing-bounce 1.2s ease-in-out infinite",
                          animationDelay: `${i * 0.15}s`,
                          "@keyframes elli-typing-bounce": {
                            "0%, 60%, 100%": { transform: "translateY(0)", opacity: 0.5 },
                            "30%": { transform: "translateY(-4px)", opacity: 1 },
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}
                <Box ref={messagesEndRef} />
              </Box>

              <Box sx={{ p: 1, display: "flex", gap: 0.75, flexWrap: "wrap", px: 1.5, pb: 1 }}>
                {(fr ? ["Sprint", "BCL", "Coachs", "Contrats"] : ["Sprint", "BCL", "Coaches", "Contracts"]).map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    onClick={() => setQuestion(label)}
                    sx={{ fontSize: 11, fontWeight: 700, bgcolor: "#f1f5f9", cursor: "pointer" }}
                  />
                ))}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 1, borderTop: "1px solid #eee" }}>
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  size="small"
                  placeholder={fr ? "Où sont les BCL ?" : "Where are the BCL?"}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleAsk(); }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5, fontSize: 13 } }}
                />
                <IconButton size="small" onClick={handleAsk} disabled={!question.trim()} sx={{ bgcolor: "#bfd56c", "&:hover": { bgcolor: "#a3c60a" } }}>
                  <SendIcon fontSize="small" sx={{ color: "#0f172a" }} />
                </IconButton>
              </Box>
            </Paper>
          </Fade>
      )}

      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          width: 56, height: 56, borderRadius: "50%", bgcolor: "#fff",
          boxShadow: "0 4px 18px rgba(15,23,42,0.22)", display: "flex",
          alignItems: "center", justifyContent: "center", cursor: "pointer",
          transition: "transform .15s", "&:hover": { transform: "scale(1.06)" },
        }}
      >
        <Elli size={44} animated={open} theme={theme} />
      </Box>
    </Box>
  );
}
